import { cn } from "@/utils/theme";
import { useGetForm } from "@/workflow_api/services/nodes.api";
import { Avatar, Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { FormVariableKeyBox } from "../key-box";
import type { FormGroupSerialized } from "..";

interface FormData {
  steps: Array<{
    id: string;
    name: string;
    sections: Array<{
      id: string;
      fields: Array<{
        id: string;
        name: string;
        label: string;
        type: string;
        config?: Record<string, string | number | boolean | object>;
      }>;
    }>;
  }>;
}

interface Props {
  formId: string;
  formName: string;
  fields: FormGroupSerialized["fields"];
}

const FormInput = ({ formId, formName, fields }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const { data, isPending } = useGetForm(formId);

  if (isPending) {
    return (
      <div className="h-10 w-full flex items-center justify-center">
        <Icon
          name="spinner-third"
          type="far"
          className="animate-spin text-lg"
        />
      </div>
    );
  }

  const formData = data.data as FormData;
  const stepsIds = new Set(Array.from(fields.values()).map((f) => f.step_id));
  const steps = formData.steps.filter((step, index) =>
    stepsIds.has(step.id || `step-${index + 1}`),
  );
  return (
    <div className="text-body">
      <div
        className={cn(
          "pl-2.5 space-x-2 flex items-center justify-start",
          isOpen ? "pb-0" : "pb-8",
        )}
      >
        <div className="bg-transparent p-1 border border-neutral-300 rounded-[12px]">
          <Avatar
            size="2xs"
            shape={null}
            className={
              "items-center justify-center border rounded-lg bg-surface-action-accent-quiet border-action-quiet text-action-quiet"
            }
          >
            <div className="">
              <Icon type="fas" name={"bars"} size="sm" className="" />
            </div>
          </Avatar>
        </div>
        <div className="flex w-full items-center space-x-2 min-w-0">
          <Badge size="sm" variant="action">
            Formulario
          </Badge>

          <div className="flex-1 font-semibold text-body-loud truncate">
            {formName}
          </div>

          <Button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle"
            size="icon"
            variant="ghost"
            className={`ml-auto mr-3 ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <Icon name="angle-up" type="fas" />
          </Button>
        </div>
      </div>
      <div
        id="content"
        className={`
        relative overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "min-h-full mb-4" : "max-h-0"}
        `}
      >
        {steps.map((s, index) => {
          const sectionIds = new Set(
            Array.from(fields.values()).map((f) => f.section_id),
          );
          const sections = s.sections.filter((section) =>
            sectionIds.has(section.id),
          );
          const fieldsToRender = sections.flatMap((section) => section.fields);
          return (
            <InputOutputBlock
              fields={fieldsToRender}
              stepName={s.name}
              index={index + 1}
              isLast={index === steps.length - 1}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FormInput;

const InputOutputBlock = ({
  fields,
  isLast,
  index,
  stepName,
}: {
  stepName: string;
  fields: FormData["steps"][number]["sections"][number]["fields"];
  isLast: boolean;
  index: number;
}) => {
  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          isLast ? "bg-transparent" : "bg-neutral-300",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-300 rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-300 rotate-x-180 rounded-tl-xl"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <h3 className="mb-4 space-x-2">
            <span className="text-body-quiet">Paso {index}</span>
            <span className="text-body-loud">{stepName}</span>
          </h3>
          <div className="space-y-3">
            {fields.map((field, index) => {
              let type = field.type;
              if (type === "file") {
                if (field?.config?.multiple) {
                  type = "array";
                } else {
                  type = "string";
                }
              }
              if (type === "select") {
                type = "string";
              }
              return (
                <FormVariableKeyBox
                  name={field.label || field.name}
                  field_id={field.id}
                  type={
                    type as "string" | "number" | "boolean" | "array" | "date"
                  }
                  key={`field-${field.name}-${index}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
